/**
 * Settings → Chat: retención y uso del contenido de chat (REQ-CHAT-042).
 * Componente presentacional para mantener la página ligera y testeable.
 */
import { Button, Card, Input, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import type { ChatUsage } from '../hooks/useChatSettings';

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

interface ChatRetentionCardProps {
  retentionDays: string;
  onRetentionChange: (value: string) => void;
  canAdmin: boolean;
  saving: boolean;
  error: string | null;
  isError?: boolean;
  usage?: ChatUsage;
  onSave: () => void;
}

export function ChatRetentionCard({
  retentionDays,
  onRetentionChange,
  canAdmin,
  saving,
  error,
  isError,
  usage,
  onSave,
}: ChatRetentionCardProps) {
  const { t } = useI18n();

  return (
    <Card className="space-y-3 p-4" data-testid="chat-retention-card">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          {t('admin.settings.chat.retentionTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('admin.settings.chat.retentionDescription')}
        </p>
      </div>
      {isError ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          {t('admin.capabilities.loadError')}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label
                htmlFor="chat-retention-days"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('admin.settings.chat.retentionDays')}
              </label>
              <Input
                id="chat-retention-days"
                data-testid="chat-retention-days"
                type="number"
                min={0}
                max={3650}
                value={retentionDays}
                disabled={!canAdmin || saving}
                onChange={(event) => onRetentionChange(event.target.value)}
                className="w-32"
              />
            </div>
            {canAdmin && (
              <Button data-testid="chat-retention-save" onClick={onSave} disabled={saving}>
                {saving && <Spinner className="mr-2 h-4 w-4" />}
                {t('admin.capabilities.save') || 'Save'}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('admin.settings.chat.retentionHint')}
          </p>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="space-y-1 border-t pt-3 text-sm text-gray-600 dark:text-gray-300">
            <p className="font-medium">{t('admin.settings.chat.usageTitle')}</p>
            <p>
              {t('admin.settings.chat.usageChat')}:{' '}
              {formatBytes(usage?.chat_content_bytes ?? 0)}
            </p>
            <p>
              {t('admin.settings.chat.usageStorage')}:{' '}
              {formatBytes(usage?.storage_used_bytes ?? 0)} /{' '}
              {usage?.storage_quota_bytes
                ? formatBytes(usage.storage_quota_bytes)
                : t('admin.settings.chat.usageUnlimitedQuota')}
            </p>
          </div>
        </>
      )}
    </Card>
  );
}
